import React from 'react';
import PropTypes from 'prop-types';

import { countriesWithoutDams, countriesWithoutIrrigatedAreas } from '../../utils';
import { TextIcon, ListIcon, WaterResIcon, WateringCanIcon, DamIcon, MapIcon } from './icons';

import styles from './styles.module.scss';

const currentLangCode = 'en';

const CountryProfile = ({ countryCode, lang, year, pdfLink, title, doNotPublishMap }) => (
  <div className={styles.container}>
    <h2>{`Country Profile - ${title}`}</h2>
    {year && <h3>Year: {year}</h3>}

    <div className={styles.info}>

      <div className={styles.factsheetContainer}>
        <h3>Factsheets</h3>

        <div className={styles.factsheet}>
          <ul className={styles.column}>
            {pdfLink && (
            <li>
              <a
                className={styles.factLink}
                href={pdfLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TextIcon />
                <span>Report {lang && `(${lang})`}</span>
              </a>
            </li>
            )}
            <li>
              <a
                className={styles.factLink}
                href={`https://storage.googleapis.com/fao-aquastat.appspot.com/countries_regions/factsheets/summary_statistics/${currentLangCode}/${countryCode}-CF.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListIcon />
                <span>Summary statistics</span>
              </a>
            </li>
            <li>
              <a
                className={styles.factLink}
                href={`https://storage.googleapis.com/fao-aquastat.appspot.com/countries_regions/factsheets/water_resources/${currentLangCode}/${countryCode}-WRS.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WaterResIcon />
                <span>Water resources</span>
              </a>
            </li>
          </ul>

          <ul className={styles.column}>
            {!countriesWithoutIrrigatedAreas.includes(countryCode) && (
            <li>
              <a
                className={styles.factLink}
                href={`https://storage.googleapis.com/fao-aquastat.appspot.com/countries_regions/factsheets/irrigation_area/${currentLangCode}/${countryCode}-IRR.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WateringCanIcon />
                <span>Irrigation areas</span>
              </a>
            </li>
            )}
            {!countriesWithoutDams.includes(countryCode) && (
              <li>
                <a
                  className={styles.factLink}
                  href={`https://storage.googleapis.com/fao-aquastat.appspot.com/Excel/dams/${countryCode}-dams_eng.xlsx`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DamIcon />
                  <span>Dams</span>
                </a>
              </li>
            )}
            {pdfLink && !doNotPublishMap && (
              <li>
                <a
                  className={styles.factLink}
                  href={`https://storage.googleapis.com/fao-aquastat.appspot.com/countries_regions/pdf/${countryCode}-map_detailed.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapIcon />
                  <span>Country Map</span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
    {pdfLink && (
      <div>
        <p>
          The country profile is a summary of key information that gives an overview of the water resources and water use at the national level.
          It can support water-related policy and decision makers in their planning and monitoring activities as well as inform researchers,
          media and the general public. Information in the report is organized by sections:
        </p>

        <ul>
          <li>Geography, Climate and Population</li>
          <li>Economy, Agriculture and Food Security</li>
          <li>Water Resources</li>
          <li>Water Use</li>
          <li>Irrigation and Drainage</li>
          <li>Water Management</li>
          <li>Policies and Legislation Related to Water Use In Agriculture</li>
          <li>Environment and Health</li>
          <li>Prospects for Agricultural Water Management</li>
        </ul>

        <p>All sources used to compile the country profile are also reported in the last section of the document.</p>

        <p>The factsheets above outline the main data by theme and map the main features of water and agriculture in the country.</p>

      </div>
    )}
    {!pdfLink && (
      <div>
        <p>
        Country profiles have not been prepared for countries of Western and Central Europe, as well as for North America (except Mexico) and Oceania (except Fiji, Samoa, Solomon Islands, Vanuatu), although the following information can be still consulted and factsheets are available for download:
        </p>
        <ul>
          <li>Country summary statistics, including information on land and population, renewable water resources, water withdrawal and irrigation areas</li>
          <li>Computation of long-term annual renewable water resources by country (in km³/year, average)</li>
          <li>Irrigation areas, including physical areas and harvested areas (in 1 000 ha)</li>
          <li>Dams</li>
        </ul>

      </div>
    )}
    <p>
      For the most recent data, please check the
      {' '}
      <a href="http://fao.org/aquastat/statistics/query/index.html?lang=en" target="_blank" rel="noopener noreferrer">
        <strong>
          Country Statistics
        </strong>
      </a>
      .
    </p>
  </div>
);

CountryProfile.propTypes = {
  countryCode: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  lang: PropTypes.string.isRequired,
  pdfLink: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default CountryProfile;
